import { useAtomValue, useSetAtom } from "jotai";
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import * as CONFIG from "../../config";
import getLoginRoute from "../../domain/loginRoute";
import { useStateValue, useUser } from "../../globalState";
import {
  useActiveInfluencer,
  useCartProducts,
  useShrinkBuffer,
} from "../../hooks";
import { hasSidebarAtom, sidebarOpenAtom } from "../../navigation/sidebarAtom";
import { selectNavLinks } from "../../navigation/useNavigationLinks";
import {
  Badge,
  Button,
  CartIcon,
  DashboardIcon,
  IconButton,
  MenuIcon as HamburgerIcon,
} from "../../themeV2";
import AccountDashboardMenu from "../AccountDashboardMenu";
import AuthModal from "../AuthModal";
import Search from "../Browse/SearchV2";
import MobileSidebarModal from "../MobileSidebarModal";
import SiteNotice from "../SiteNotice";
import css from "./Navbar.module.css";

const Navbar = React.memo(function Navbar() {
  const history = useHistory();
  const [{ user, triggerAuthModal }] = useStateValue();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { cartProducts } = useCartProducts();

  // mobile sidebar vars
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const { showMobileNav } = useShrinkBuffer();

  const setSidebarOpen = useSetAtom(sidebarOpenAtom);
  const hasSidebar = useAtomValue(hasSidebarAtom);
  const guardUser = useUser();
  const activeInfluencer = useActiveInfluencer();
  const hasMobileNav =
    hasSidebar ||
    Boolean(activeInfluencer) ||
    selectNavLinks("mobile", { user: guardUser, activeInfluencer }).length > 0;

  const handleHamburgerClick = useCallback(() => {
    if (hasSidebar) {
      setSidebarOpen((prev) => !prev);
    } else {
      setShowMobileSidebar((prev) => !prev);
    }
  }, [hasSidebar, setSidebarOpen]);

  const CloseAuthModal = (user) => {
    setShowAuthModal(false);
    setShowMobileSidebar(false);

    if (!user || !Array.isArray(user?.roles)) {
      return;
    }

    const route = getLoginRoute(user);
    if (route) history.push(route);
    else window.location.reload(); // is this necessary anymore? TODO
  };

  const Login = useCallback(() => {
    setShowAuthModal(true);
    setIsLoggingIn(true);
  }, []);

  useEffect(() => {
    if (triggerAuthModal) {
      if (triggerAuthModal.type.toLowerCase() === "signup") {
        setIsLoggingIn(false);
      } else {
        setIsLoggingIn(true);
      }
      setShowAuthModal(true);
    }
  }, [triggerAuthModal]);

  return (
    <>
      <a href="#main" className={css.skipLink}>
        Skip to main content
      </a>
      <header className={css.navbar}>
        {showMobileNav &&
          hasMobileNav &&
          !user.is_approval_pending &&
          user.user_id && (
            <IconButton
              label="Open navigation menu"
              className={css.hamburger}
              onClick={handleHamburgerClick}
            >
              <HamburgerIcon />
            </IconButton>
          )}

        <NavLink to="/" className={css.logoLink}>
          <img
            className={css.logo}
            width="108"
            height="40"
            alt="Streamily home"
            src={CONFIG.SITE_LOGO}
          />
        </NavLink>

        <div role="search" className={css.search}>
          <Search />
        </div>

        <div className={css.right}>
          {!showMobileNav &&
            !user.is_approval_pending &&
            user.show_dashboard_link && (
              <Button
                as={NavLink}
                variant="text"
                className={css.dashboardLink}
                to="/Dashboard/MyDashboard"
                leading={<DashboardIcon />}
              >
                <span className={css.dashboardLinkText}>Dashboard</span>
              </Button>
            )}

          <div className={css.cartWrapper}>
            <IconButton
              label="Open cart"
              onClick={() => {
                document.dispatchEvent(new CustomEvent("cart:open"));
              }}
            >
              <CartIcon />
            </IconButton>
            {cartProducts.length > 0 && (
              <Badge
                className={css.cartBadge}
                variant="count"
                count={cartProducts.length}
              />
            )}
          </div>

          {!user.is_approval_pending && user.user_id ? (
            <AccountDashboardMenu user={user} />
          ) : user.is_approval_pending ? (
            <>
              <Button
                as={NavLink}
                variant="text"
                className={css.textLink}
                to="/talent/pending"
              >
                Dashboard
              </Button>
              <AccountDashboardMenu user={user} />
            </>
          ) : (
            <Button variant="text" onClick={Login}>
              Login
            </Button>
          )}
        </div>
      </header>

      {showAuthModal && (
        <AuthModal onClose={CloseAuthModal} isLoggingIn={isLoggingIn} />
      )}

      {showMobileSidebar && !hasSidebar && (
        <MobileSidebarModal onClose={CloseAuthModal} />
      )}

      <SiteNotice />
    </>
  );
});

export default Navbar;
